
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

import { GradientPoint } from "@/types/colors";
import { EasyEvent } from "@/util/easy-event";

import {
  getRightValue,
  rgbToHsv,
  generateGradientStyle,
  generateSvgStyle
} from "@/components/Reusable/CustomInputs/3rdPartyWrappers/Gradient/helpers";
import Preview from "@/components/Reusable/CustomInputs/3rdPartyWrappers/Gradient/Preview/Preview.vue";
import GradientControls from "@/components/Reusable/CustomInputs/3rdPartyWrappers/Gradient/GradientControls.vue";
import Area from "@/components/Reusable/CustomInputs/3rdPartyWrappers/Gradient/Area/Area.vue";
import defensiveCopy from "@/util/defensive-copy";

type ActionName = "onStartChange" | "onChange" | "onEndChange";

@Component({
  components: {
    Area,
    GradientControls,
    Preview
  }
})
export default class Gradient extends Vue {
  @Prop({ default: "linear" })
  type!: string;

  @Prop({ default: 0 })
  degree!: number;

  @Prop({
    default: () => {
      return [
        {
          left: 0,
          red: 0,
          green: 0,
          blue: 0,
          alpha: 1
        },
        {
          left: 100,
          red: 255,
          green: 0,
          blue: 0,
          alpha: 1
        }
      ];
    }
  })
  points!: GradientPoint[];

  @Prop()
  onStartChange!: Function;

  @Prop()
  onChange!: Function;

  @Prop()
  onEndChange!: Function;

  activePointIndex = 0;

  get activePoint() {
    return this.points[this.activePointIndex];
  }

  get colorRed() {
    return this.activePoint.red;
  }

  get colorGreen() {
    return this.activePoint.green;
  }

  get colorBlue() {
    return this.activePoint.blue;
  }

  get colorAlpha() {
    return this.activePoint.alpha;
  }

  get activePointHsv() {
    const { red, green, blue } = this.activePoint;
    return rgbToHsv({ red, green, blue });
  }

  colorHue = this.activePointHsv.hue;

  get colorSaturation() {
    return this.activePointHsv.saturation;
  }

  get colorValue() {
    return this.activePointHsv.value;
  }

  isShiftActive = false;

  actions = {
    onStartChange: this.onStartChange,
    onChange: this.onChange,
    onEndChange: this.onEndChange
  };

  easyEvent = new EasyEvent();

  @Watch("points", { deep: true })
  onPointsChange() {
    if (this.activePointIndex >= this.points.length) {
      this.activePointIndex = this.points.length - 1;
    }
  }

  mounted() {
    this.easyEvent.on("keyup", this.keyUpHandler);
    this.easyEvent.on("keydown", this.keyDownHandler);
  }

  beforeDestroy() {
    this.easyEvent.removeAll();
  }

  removePoint(index = this.activePointIndex) {
    if (this.points.length <= 2) {
      return;
    }

    const newPoints = this.points.filter((_, i) => i !== index);

    if (index > 0) {
      this.activePointIndex = index - 1;
    }

    this.onChange &&
      this.onChange({
        points: newPoints,
        type: this.type,
        degree: this.degree,
        svgValue: generateSvgStyle(this.points, this.type, this.degree),
        value: generateGradientStyle(this.points, this.type, this.degree)
      });
  }

  keyUpHandler(event: KeyboardEvent) {
    this.isShiftActive = event.shiftKey;

    // Delete / Backspace Key
    if (event.keyCode === 46 || event.keyCode === 8) {
      this.removePoint(this.activePointIndex);
    }
  }

  keyDownHandler(event: KeyboardEvent) {
    this.isShiftActive = event.shiftKey;
  }

  changeActivePointIndex(index: number) {
    this.activePointIndex = index;
    this.colorHue = this.activePointHsv.hue;
  }

  changeGradientControl({ type, degree }: { type: string; degree?: number }) {
    const newType = getRightValue(type, this.type);
    const newDegree = getRightValue(degree, this.degree);

    this.onChange({
      points: this.points,
      type: newType,
      degree: newDegree,
      svgValue: generateSvgStyle(this.points, newType, newDegree),
      value: generateGradientStyle(this.points, newType, newDegree)
    });
  }

  updateColor(
    {
      red,
      green,
      blue,
      alpha,
      hue
    }: {
      red: number;
      green: number;
      blue: number;
      alpha: number;
      hue: number;
      saturation: number;
      value: number;
    },
    actionName: ActionName = "onChange"
  ) {
    red = getRightValue(red, this.colorRed);
    green = getRightValue(green, this.colorGreen);
    blue = getRightValue(blue, this.colorBlue);
    alpha = getRightValue(alpha, this.colorAlpha);
    hue = getRightValue(hue, this.colorHue);

    this.colorHue = hue;

    const newPoints = defensiveCopy(this.points);

    newPoints[this.activePointIndex] = {
      ...newPoints[this.activePointIndex],
      red,
      green,
      blue,
      alpha
    };

    const action = this.actions[actionName];

    action &&
      action({
        points: newPoints,
        type: this.type,
        degree: this.degree,
        svgValue: generateSvgStyle(newPoints, this.type, this.degree),
        value: generateGradientStyle(newPoints, this.type, this.degree)
      });
  }

  updateGradientLeft(
    left: number,
    index: number,
    actionName: ActionName = "onChange"
  ) {
    const newPoints = defensiveCopy(this.points);
    newPoints[index].left = left;

    const action = this.actions[actionName];

    action &&
      action({
        points: newPoints,
        type: this.type,
        degree: this.degree,
        svgValue: generateSvgStyle(newPoints, this.type, this.degree),
        value: generateGradientStyle(newPoints, this.type, this.degree)
      });
  }

  addPoint(left: number) {
    const newPoints = defensiveCopy(this.points);

    newPoints.push({
      ...this.points[this.activePointIndex],
      left
    });

    this.onChange &&
      this.onChange({
        points: newPoints,
        type: this.type,
        degree: this.degree,
        svgValue: generateSvgStyle(newPoints, this.type, this.degree),
        value: generateGradientStyle(newPoints, this.type, this.degree)
      });

    this.activePointIndex = newPoints.length - 1;
  }
}
